/* Progressive bundle discounts + free shipping bar.
   Faixas por QUANTIDADE: 2/3/4 peças. Frete grátis + brinde: por VALOR.

   🔴 Todanua, 06/09/2026 — o limite do frete grátis é POR MOEDA, e não
   mais um `80` solto. Antes, a barra somava o carrinho na moeda exibida e
   comparava com 80: no Brasil isso destravava em R$ 80 (US$ 15,37) e no
   Reino Unido só em £ 80 (US$ 106,56). Ver LIMITE_POR_MOEDA.
   
   This bar listens to cart updates via multiple mechanisms:
   1. 'cart:updated' custom event (fired by the Ella theme after qty changes)
   2. MutationObserver on the cart drawer (catches section re-renders)
   3. Periodic fetchCart as failsafe
   4. 'cart-drawer-items' attribute changes
*/

/* ------------------------------------------------------------------
   CONFIG: products excluded from bundle discount tiers
------------------------------------------------------------------ */
const REWARDS_EXCLUDED_HANDLES = [
  '7-nights-to-drive-him-wild',
];
const REWARDS_EXCLUDED_TITLE_PATTERNS = [
  /7\s*nights\s*to\s*drive/i,
];

/* ------------------------------------------------------------------
   Limite do frete grátis + brinde, POR MOEDA.

   São números redondos de propósito, não a conversão crua de US$ 80
   (que daria R$ 416,31, A$ 112,75, £ 60,06). Uma promoção precisa de um
   número que se anuncie. Todos ficam um pouco ABAIXO dos 80 dólares:
   numa promoção, errar para o lado generoso vende.

     USD  80   = US$ 80,00 (exato)
     BRL  399  = US$ 76,67
     AUD  109  = US$ 77,34
     GBP  59   = US$ 78,58

   Moeda fora da tabela cai no arredondamento automático de
   _arredondaBonito() — nunca volta a comparar real com dólar.
------------------------------------------------------------------ */
const LIMITE_POR_MOEDA = {
  USD: 80,
  BRL: 399,
  AUD: 109,
  GBP: 59,
};

class ProgressRewardsBar extends HTMLElement {
  constructor() {
    super();
    this._boundHandleCartUpdate = this._onCartEvent.bind(this);
    this._lastCartJSON = '';
    this._giftSyncInProgress = false;
    this._cachedGiftVariantId = null;
    this._debounceTimer = null;
  }

  // Global lock: prevents gift sync loops when the drawer is re-rendered
  // after we add/remove the gift (which causes a new element to be created).
  static get _globalLock() {
    return window.__rewardsBarGiftLock || false;
  }
  static set _globalLock(v) {
    window.__rewardsBarGiftLock = v;
  }

  connectedCallback() {
    try {
      this.tiers = JSON.parse(this.dataset.itemTiers || '[]');
    } catch (e) {
      this.tiers = [];
    }
    // 🔴 O `data-shipping-threshold` do tema está em DÓLAR. Aqui ele vira
    // o limite na moeda que a cliente está vendo — senão R$ 80 passaria
    // por US$ 80 e o brinde sairia por um quinto do preço.
    this.shippingThreshold = this._limiteLocal(
      parseFloat(this.dataset.shippingThreshold || '80'));

    // 📌 Item 5 da fila: o lembrete do eBook na reta final.
    // Convertido pelo câmbio, e NÃO pela tabela de limites redondos: isto não é
    // promessa anunciada, é só o gatilho de um aviso — não precisa ser bonito.
    this.ebookReminder = this._cambioSimples(
      parseFloat(this.dataset.ebookReminderFrom || '20'));
    this.ebookTitle = this.dataset.ebookTitle || '';
    this.shippingPosition = parseFloat(this.dataset.shippingPosition || '100');
    this.fillEl = this.querySelector('[data-rewards-fill]');
    this.truckEl = this.querySelector('[data-rewards-truck]');
    this.messageEl = this.querySelector('[data-rewards-message]');
    this.trackEl = this.querySelector('.rewards-bar__track');
    this.milestoneEls = Array.from(this.querySelectorAll('.rewards-bar__milestone'));

    // Listen to cart events
    document.addEventListener('cart:updated', this._boundHandleCartUpdate);
    document.addEventListener('cart-update', this._boundHandleCartUpdate);

    // Watch for drawer DOM changes (the theme replaces innerHTML on cart updates)
    this._setupMutationObserver();

    // Initial fetch
    this._fetchAndUpdate();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this._boundHandleCartUpdate);
    document.removeEventListener('cart-update', this._boundHandleCartUpdate);
    if (this._observer) this._observer.disconnect();
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  _setupMutationObserver() {
    // Observe the cart-drawer element (the custom element itself is stable;
    // only its innerHTML changes). When the theme replaces innerHTML via
    // renderContents() or updateSections(), this fires so we re-fetch.
    const drawer = document.querySelector('cart-drawer');
    const target = drawer || document.body;

    this._observer = new MutationObserver(() => {
      this._debouncedFetch();
    });
    this._observer.observe(target, { childList: true, subtree: true });
  }

  _debouncedFetch() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this._fetchAndUpdate();
    }, 150);
  }

  _onCartEvent(e) {
    // The Ella theme passes the full cart JSON as event.detail
    const cart = e && e.detail;
    if (cart && typeof cart.item_count !== 'undefined' && Array.isArray(cart.items)) {
      this._processCart(cart);
    } else {
      this._debouncedFetch();
    }
  }

  _fetchAndUpdate() {
    fetch('/cart.js', { 
      headers: { 'Accept': 'application/json' },
      cache: 'no-store' 
    })
      .then((r) => r.json())
      .then((cart) => this._processCart(cart))
      .catch(() => {});
  }

  _processCart(cart) {
    if (!cart) return;
    // Deduplicate: skip if cart hasn't changed
    const sig = cart.item_count + ':' + (cart.total_price || 0) + ':' + (cart.items || []).length;
    if (sig === this._lastCartJSON) return;
    this._lastCartJSON = sig;

    this._updateGameGift(cart);
    this._updateBar(cart);
    // The eBook promotion has been replaced; game access is fulfilled separately.
  }

  _updateGameGift(cart) {
    const qty = (cart.items || []).reduce((n, item) => n + (item.requires_shipping && !this._isEbookGift(item) && item.handle !== 'gift-wrapping' ? item.quantity : 0), 0);
    for (const banner of document.querySelectorAll('[data-fire-gift]')) {
      if (banner.dataset.giftContext === 'product') continue;
      const unlocked = qty >= 2;
      banner.hidden = qty === 0;
      banner.dataset.qualifyingPieces = String(qty);
      banner.dataset.unlocked = String(unlocked);
      const fields = [
        ['[data-gift-badge]', unlocked ? 'YOUR FREE GAME IS SECURED' : 'ONE MORE PIECE, A FREE GAME'],
        ['[data-gift-status]', unlocked ? 'Your free game is secured!' : 'Add 1 more piece to get your free game.'],
        ['[data-gift-note]', unlocked ? 'Included with your purchase.' : 'Free with 2 or more pieces.']
      ];
      for (const [selector, text] of fields) {
        const element = banner.querySelector(selector);
        if (element && element.textContent !== text) element.textContent = text;
      }
      const check = banner.querySelector('[data-gift-check]');
      if (check) check.hidden = !unlocked;
    }
  }

  /* ---- Product detection ---- */

  _isEbookGift(item) {
    if (!item) return false;
    const handle = (
      item.handle ||
      item.product_handle ||
      (item.url ? String(item.url).split('?')[0].split('/').filter(Boolean).pop() : '')
    ).toLowerCase();
    if (handle && REWARDS_EXCLUDED_HANDLES.indexOf(handle) !== -1) return true;
    const title = String(item.product_title || item.title || '');
    for (const re of REWARDS_EXCLUDED_TITLE_PATTERNS) {
      if (re.test(title)) return true;
    }
    return false;
  }

  _countQualifyingItems(cart) {
    if (!cart || !cart.items) return 0;
    let qty = 0;
    for (const item of cart.items) {
      if (this._isEbookGift(item)) continue;
      qty += item.quantity || 0;
    }
    return qty;
  }

  _getQualifyingSubtotal(cart) {
    if (!cart || !cart.items) return 0;
    let cents = 0;
    for (const item of cart.items) {
      if (this._isEbookGift(item)) continue;
      cents += item.final_line_price || 0;
    }
    return cents / 100;
  }

  /* Qual moeda a cliente está vendo agora. */
  _moedaAtiva() {
    const c = (window.Shopify && window.Shopify.currency) || {};
    return c.active || 'USD';
  }

  /* O limite do frete grátis na moeda da cliente.

     🔴 Número da tabela primeiro; só quem não estiver nela é convertido e
     arredondado. Fixar o valor local é de propósito: a promessa da promoção
     não pode mudar de valor toda vez que o câmbio mexe. */
  _limiteLocal(baseUSD) {
    const moeda = this._moedaAtiva();
    if (LIMITE_POR_MOEDA[moeda] != null) return LIMITE_POR_MOEDA[moeda];
    const c = (window.Shopify && window.Shopify.currency) || {};
    const taxa = parseFloat(c.rate || '1') || 1;
    return this._arredondaBonito(baseUSD * taxa);
  }

  /* Conversão crua para a moeda da cliente, sem arredondar. */
  _cambioSimples(valorUSD) {
    const c = (window.Shopify && window.Shopify.currency) || {};
    const taxa = parseFloat(c.rate || '1') || 1;
    return valorUSD * taxa;
  }

  /* 112,75 vira 109; 416,31 vira 419; 1.488 vira 1.499. Terminar em 9 é o
     que o comércio faz — e um limite quebrado ("gaste mais R$ 37,31") não
     convence ninguém a somar uma peça. */
  _arredondaBonito(v) {
    if (!(v > 0)) return v;
    const passo = v < 50 ? 5 : (v < 500 ? 10 : 100);
    return Math.max(passo - 1, Math.round(v / passo) * passo - 1);
  }

  /* Formata na moeda da cliente. Antes isto tinha 'USD' fixo, então um valor
     em real aparecia com cifrão de dólar. */
  _formatMoney(value) {
    const moeda = this._moedaAtiva();
    try {
      // Locale `undefined` = o do NAVEGADOR da cliente. Passar o `lang` do
      // tema (que é "en") formatava real como "R$37.50"; brasileiro escreve
      // "R$ 37,50", e esse detalhe é o que separa loja séria de gambiarra.
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: moeda,
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      return value.toFixed(2) + ' ' + moeda;
    }
  }

  /* ---- Bar UI update ---- */

  _updateBar(cart) {
    const itemCount = this._countQualifyingItems(cart);
    const subtotal = this._getQualifyingSubtotal(cart);

    // Hide bar if no qualifying items (the ebook alone doesn't count)
    if (itemCount === 0) {
      this.classList.add('rewards-bar--empty');
      return;
    }
    this.classList.remove('rewards-bar--empty');

    const unlockedTiers = this.tiers.filter((t) => itemCount >= t.qty);
    const shippingUnlocked = itemCount >= 3 || subtotal >= this.shippingThreshold;

    let fill = 0;
    let nextMessage = '';

    const lastUnlockedItemTier = unlockedTiers[unlockedTiers.length - 1];
    const nextItemTier = this.tiers.find((t) => itemCount < t.qty);

    if (lastUnlockedItemTier) fill = lastUnlockedItemTier.position;

    if (nextItemTier) {
      const prevQty = lastUnlockedItemTier ? lastUnlockedItemTier.qty : 0;
      const prevPos = lastUnlockedItemTier ? lastUnlockedItemTier.position : 0;
      const span = nextItemTier.position - prevPos;
      const progress = Math.min(1, (itemCount - prevQty) / (nextItemTier.qty - prevQty));
      fill = prevPos + span * progress;
      const itemsToGo = nextItemTier.qty - itemCount;
      nextMessage =
        'Add <strong>' + itemsToGo + ' more item' + (itemsToGo === 1 ? '' : 's') +
        '</strong> to unlock <strong>' + nextItemTier.label + '</strong>';
    } else if (!shippingUnlocked) {
      const lastItemTier = this.tiers[this.tiers.length - 1];
      const prevPos = lastItemTier ? lastItemTier.position : 0;
      const span = this.shippingPosition - prevPos;
      const progress = Math.min(1, subtotal / this.shippingThreshold);
      fill = prevPos + span * progress;
      const remaining = Math.max(0, this.shippingThreshold - subtotal);
      nextMessage =
        'Spend <strong>' + this._formatMoney(remaining) +
        '</strong> more to unlock <strong>FREE Shipping</strong>';
      // Só na reta final (abaixo de ~US$ 20 que faltam). Se aparecesse sempre
      // viraria paisagem, e o que se quer aqui é o último empurrão.
      if (this.ebookTitle && remaining > 0 && remaining <= this.ebookReminder) {
        nextMessage +=
          '<span class="rewards-bar__ebook">Almost there — you also get the ' +
          this.ebookTitle + ' eBook, free</span>';
      }
    } else {
      fill = 100;
      nextMessage = '<span class="rewards-bar__success">All rewards unlocked! Free shipping + 25% OFF</span>';
    }

    if (itemCount >= 4) {
      nextMessage = '<strong>25% OFF + FREE shipping + FREE game</strong><br>All rewards unlocked';
    } else if (itemCount === 3) {
      nextMessage = '<strong>20% OFF + FREE shipping + FREE game</strong><br>Add 1 more piece for 25% OFF';
    } else if (itemCount === 2) {
      nextMessage = '<strong>15% OFF + your FREE game secured</strong><br>Add 1 more piece for 20% OFF + FREE shipping';
    } else {
      nextMessage = '<strong>Add 1 more piece</strong><br>Unlock 15% OFF + your FREE game';
    }

    fill = Math.max(0, Math.min(100, fill));
    if (this.fillEl) this.fillEl.style.width = fill + '%';
    if (this.truckEl) this.truckEl.style.left = fill + '%';
    if (this.trackEl) this.trackEl.setAttribute('aria-valuenow', String(Math.round(fill)));
    if (this.messageEl) this.messageEl.innerHTML = nextMessage;

    this.milestoneEls.forEach((el) => {
      const pos = parseFloat(el.dataset.tierPosition);
      const isShipping = el.classList.contains('rewards-bar__milestone--shipping');
      const tier = this.tiers.find((t) => t.position === pos);
      const unlocked = isShipping ? shippingUnlocked : (tier ? itemCount >= tier.qty : false);
      el.classList.toggle('is-unlocked', unlocked);
    });
  }

  /* ---- Gift auto-add / auto-remove ---- */

  async _syncGiftLine(cart) {
    if (this._giftSyncInProgress) return;
    if (ProgressRewardsBar._globalLock) return; // prevent re-entry after gift-triggered re-renders
    if (!cart || !cart.items) return;

    const subtotal = this._getQualifyingSubtotal(cart);
    const qualifyingItems = this._countQualifyingItems(cart);
    const giftLine = cart.items.find((it) => this._isEbookGift(it));
    const shouldHaveGift = subtotal >= this.shippingThreshold && qualifyingItems > 0;

    if (shouldHaveGift && !giftLine) {
      // Need to add the gift
      this._giftSyncInProgress = true;
      ProgressRewardsBar._globalLock = true;
      try {
        const variantId = await this._getGiftVariantId();
        if (variantId) {
          await this._cartAdd(variantId);
          // Release lock after drawer has had time to re-render
          setTimeout(() => {
            this._giftSyncInProgress = false;
            ProgressRewardsBar._globalLock = false;
            this._lastCartJSON = '';
            this._fetchAndUpdate();
          }, 2000);
          return;
        }
      } catch (e) {
        console.warn('[rewards-bar] gift add failed', e);
      }
      this._giftSyncInProgress = false;
      ProgressRewardsBar._globalLock = false;
    } else if (giftLine && !shouldHaveGift) {
      // Need to remove the gift
      this._giftSyncInProgress = true;
      ProgressRewardsBar._globalLock = true;
      try {
        await this._cartRemove(giftLine.key);
        setTimeout(() => {
          this._giftSyncInProgress = false;
          ProgressRewardsBar._globalLock = false;
          this._lastCartJSON = '';
          this._fetchAndUpdate();
        }, 2000);
        return;
      } catch (e) {
        console.warn('[rewards-bar] gift remove failed', e);
      }
      this._giftSyncInProgress = false;
      ProgressRewardsBar._globalLock = false;
    }
  }

  async _getGiftVariantId() {
    if (this._cachedGiftVariantId) return this._cachedGiftVariantId;
    const handle = REWARDS_EXCLUDED_HANDLES[0];
    if (!handle) return null;
    try {
      const res = await fetch('/products/' + handle + '.js', {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      this._cachedGiftVariantId =
        (data.variants && data.variants[0] && data.variants[0].id) || null;
      return this._cachedGiftVariantId;
    } catch (e) {
      return null;
    }
  }

  async _cartAdd(variantId) {
    // Include sections so the theme can re-render the drawer
    const sectionIds = this._getDrawerSectionIds();
    const body = {
      items: [{ id: variantId, quantity: 1 }],
    };
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(
      (window.routes && window.routes.cart_add_url) || '/cart/add.js',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) throw new Error('add failed');
    const data = await res.json();
    this._triggerDrawerRefresh(data.sections);
    return data;
  }

  async _cartRemove(lineKey) {
    const sectionIds = this._getDrawerSectionIds();
    const body = {
      id: lineKey,
      quantity: 0,
    };
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(
      (window.routes && window.routes.cart_change_url) || '/cart/change.js',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) throw new Error('remove failed');
    const data = await res.json();
    this._triggerDrawerRefresh(data.sections);
    return data;
  }

  _getDrawerSectionIds() {
    const ids = new Set();
    document
      .querySelectorAll('cart-drawer-items[data-section-id], cart-items-component[data-section-id]')
      .forEach((el) => ids.add(el.dataset.sectionId));
    return Array.from(ids);
  }

  _triggerDrawerRefresh(sections) {
    // Let the theme's own cart component handle the section re-render
    if (sections) {
      const drawerItems =
        document.querySelector('cart-drawer-items') ||
        document.querySelector('cart-items-component');
      if (drawerItems && typeof drawerItems.updateSections === 'function') {
        drawerItems.updateSections(sections);
      }
    }
    // Also update the cart icon bubble
    const bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleEl && sections && sections['cart-icon-bubble']) {
      const tmp = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newBubble = tmp.querySelector('#cart-icon-bubble');
      if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
    }
  }
}

if (!customElements.get('progress-rewards-bar')) {
  customElements.define('progress-rewards-bar', ProgressRewardsBar);
}
